/**
 * `<caos-stepper>` — the steps of a guided flow, which one you are on, and which are done.
 * (Catalog: Status & feedback › Stepper — the steps of a guided flow, which one you are on, and which are done.)
 *
 * THE PIECE THE WIZARD SURFACE WAS MISSING. The page-template catalogue declares a `wizard` surface
 * kind whose blueprint is a pinned rail beside the active step's content, and until now nothing
 * could draw that rail — a declared shape with no component behind it (CAOS-598). This is that
 * rail. It draws the sequence and says where the person is; it does not own the step's content,
 * which is what the blueprint's `main` region is for.
 *
 * A STEP IS A SHAPE, NOT A COMPONENT. `steps` is a list of plain objects set as a property, exactly
 * as `<caos-tab-strip>` takes its tabs, and plain strings are accepted as shorthand. There is no
 * `<caos-step>` element and there will not be one: two things that differ by their data are two
 * configurations of one piece. The incumbent ships the indicator and its step as separate
 * components; the catalogue survey took the first and refused the second for this reason.
 *
 * STATE IS THE PLATFORM'S VOCABULARY, NOT THIS PIECE'S. A completed step reads `success`, a failed
 * one reads `danger`, and the one you are on carries the accent and the selected-state tint the
 * design system already defines. Nothing here introduces a token naming this component, which is
 * the rule the token axes work set (CAOS-586): a missing expression is a missing AXIS, added once
 * for everyone, never a token added for one piece.
 *
 * STATE IS DECLARED OR DERIVED, AND DECLARED WINS. A step that says nothing about itself is read
 * from its position: before the current one it is completed, at it it is current, after it it is
 * still to come. That is what makes the ordinary case — a flow moving forward one step at a time —
 * a single `current` assignment rather than a rewrite of the whole list. A step that DOES declare a
 * state keeps it, which is the only way `error` can ever be said: nothing about a step's position
 * can tell you it failed.
 *
 * Styling seams: `::part(list)`, `::part(step)`, `::part(marker)`, `::part(label)`,
 * `::part(description)`, `::part(connector)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

/** What a step may say about itself. `completed`, `current` and `error` are the declared states. */
export type CaosStepState = 'completed' | 'current' | 'upcoming' | 'error';

/** One step of a guided flow — a shape in the stepper's list, never an element of its own. */
export interface CaosStep {
  /** Stable identity. What `current` names, and what a selection reports. */
  key: string;
  /** What the step is called. */
  label: string;
  /** One quieter line under the label — what the step is for, where the label alone is thin. */
  description?: string;
  /**
   * The step's state when it is not the one position implies. Declared beats derived, and `error`
   * can only ever be declared: no amount of knowing where a step sits says that it failed.
   */
  state?: CaosStepState;
}

const STATES: ReadonlySet<string> = new Set<CaosStepState>(['completed', 'current', 'upcoming', 'error']);

/**
 * The piece's own sheet, as TEXT. Read by its tests as well as through the adopted sheet: a DOM
 * with no layout engine drops a declaration whose value wraps `var()` in `calc()` — the
 * connector offset is exactly that shape — so a token read only there would vanish from
 * `cssRules` and a completeness check over them would narrow without saying so.
 */
export const STEPPER_STYLES = `
  :host { display: block; }

  .list {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  :host([orientation="horizontal"]) .list { flex-direction: row; align-items: flex-start; }

  .step {
    position: relative;
    display: flex;
    gap: var(--caos-space-3);
    padding: var(--caos-space-2) var(--caos-space-2);
    border-radius: var(--caos-radius-sm);
    min-width: 0;
  }
  :host([orientation="horizontal"]) .step { flex: 1 1 0; flex-direction: column; align-items: center; text-align: center; }

  /* The step you are on: the accent, and the selected tint the system already defines. */
  .step[data-state="current"] { background: var(--caos-state-selected-bg); }

  /* A step that can be gone back to is a real control; one that cannot is not pretending to be. */
  button.step {
    appearance: none;
    border: none;
    background: transparent;
    font: inherit;
    text-align: inherit;
    width: 100%;
    cursor: pointer;
  }
  button.step:hover { background: var(--caos-state-hover-bg); }
  button.step:focus-visible { outline: var(--caos-focus-ring); outline-offset: 2px; }

  /* The rail offers going back; the steps still ahead visibly do not offer it. Said only
     while something IS offered — with no interaction on the rail there is no distinction
     to draw, and dimming half a report would be drawing one that is not there. */
  :host([interactive]) .step[data-state="upcoming"] { opacity: var(--caos-state-disabled-opacity); }

  .marker {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: var(--caos-radius-pill);
    border: 1px solid var(--caos-color-border);
    color: var(--caos-color-text-muted);
    background: var(--caos-color-surface);
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }
  .marker svg { width: 13px; height: 13px; }

  .step[data-state="completed"] .marker { color: var(--caos-color-success); border-color: var(--caos-color-success); }
  .step[data-state="error"] .marker { color: var(--caos-color-danger); border-color: var(--caos-color-danger); }
  .step[data-state="current"] .marker {
    color: var(--caos-color-accent);
    border-color: var(--caos-color-accent);
    box-shadow: inset 0 0 0 1px var(--caos-color-accent);
  }

  .body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }

  .label {
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
  }
  .step[data-state="current"] .label { color: var(--caos-color-text); }
  .step[data-state="completed"] .label { color: var(--caos-color-text); }
  .step[data-state="error"] .label { color: var(--caos-color-danger); }

  .description {
    font-family: var(--caos-font);
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--caos-color-text-muted);
  }
  .description:empty { display: none; }

  /* The line joining one step to the next. Decorative: it carries no meaning a reader needs. */
  .connector {
    flex: none;
    background: var(--caos-color-border);
    align-self: stretch;
  }
  :host(:not([orientation="horizontal"])) .connector { width: 1px; margin-left: calc(var(--caos-space-2) + 11px); height: var(--caos-space-3); }
  :host([orientation="horizontal"]) .connector { height: 1px; margin-top: 11px; flex: 1 1 auto; }
  .connector[data-state="completed"] { background: var(--caos-color-success); }
`;

const sheet = makeSheet(STEPPER_STYLES);

/** `['a','b']` and `[{key,label}]` both mean the same thing; the shorthand is expanded once, here. */
function toStep(entry: CaosStep | string, index: number): CaosStep {
  if (typeof entry === 'string') return { key: entry, label: entry };
  const key = typeof entry.key === 'string' && entry.key.length > 0 ? entry.key : String(index + 1);
  const label = typeof entry.label === 'string' && entry.label.length > 0 ? entry.label : key;
  const state = typeof entry.state === 'string' && STATES.has(entry.state) ? entry.state : undefined;
  const description = typeof entry.description === 'string' ? entry.description : undefined;
  return state === undefined
    ? description === undefined
      ? { key, label }
      : { key, label, description }
    : description === undefined
      ? { key, label, state }
      : { key, label, description, state };
}

export class CaosStepper extends CaosElement {
  static get observedAttributes(): string[] {
    return ['current', 'orientation', 'interactive', 'label'];
  }

  private stepList: CaosStep[] = [];
  private readonly listEl: HTMLElement;

  constructor() {
    super(sheet);
    this.listEl = document.createElement('ol');
    this.listEl.className = 'list';
    this.listEl.setAttribute('part', 'list');
    this.root.append(this.listEl);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** The steps, in order. Set as a property: a list cannot ride an attribute. */
  get steps(): CaosStep[] {
    return this.stepList.map((s) => ({ ...s }));
  }

  set steps(list: Array<CaosStep | string>) {
    this.stepList = Array.isArray(list) ? list.map(toStep) : [];
    this.render();
  }

  /** The key of the step being worked on. Reflected, so it can be read back off the element. */
  get current(): string {
    return this.getAttribute('current') ?? '';
  }

  set current(key: string) {
    if (key) this.setAttribute('current', key);
    else this.removeAttribute('current');
  }

  /**
   * The state each step is drawn in: what it declared, or what its position implies.
   *
   * Exposed because it is the whole judgement this piece makes, and a test that re-derives it is
   * testing its own copy of the rule rather than the one that draws.
   */
  stateOf(index: number): CaosStepState {
    const step = this.stepList[index];
    if (!step) return 'upcoming';
    if (step.state !== undefined) return step.state;
    const at = this.stepList.findIndex((s) => s.key === this.current);
    if (at === -1) return 'upcoming';
    if (index < at) return 'completed';
    return index === at ? 'current' : 'upcoming';
  }

  private render(): void {
    const interactive = this.hasAttribute('interactive');
    const name = this.getAttribute('label');
    if (name) this.listEl.setAttribute('aria-label', name);
    else this.listEl.removeAttribute('aria-label');

    const drawn: HTMLElement[] = [];
    this.stepList.forEach((step, i) => {
      const state = this.stateOf(i);
      const item = document.createElement('li');

      // A step that can be returned to is a button; one that cannot is not dressed as a control.
      const reachable = interactive && (state === 'completed' || state === 'error');
      const inner = document.createElement(reachable ? 'button' : 'div');
      inner.className = 'step';
      inner.setAttribute('part', 'step');
      inner.dataset['state'] = state;
      inner.dataset['key'] = step.key;
      if (reachable) {
        (inner as HTMLButtonElement).type = 'button';
        inner.addEventListener('click', () => {
          this.dispatchEvent(
            new CustomEvent('caos-step-select', { bubbles: true, composed: true, detail: { key: step.key, index: i } }),
          );
        });
      }
      if (state === 'current') inner.setAttribute('aria-current', 'step');

      const marker = document.createElement('span');
      marker.className = 'marker';
      marker.setAttribute('part', 'marker');
      // The marks are the platform's, not this piece's: a completed step wears the success glyph
      // and a failed one the danger glyph. A step still to come is drawn as its own number, which
      // is the only thing there is to say about it.
      if (state === 'completed') marker.append(makeIcon('success'));
      else if (state === 'error') marker.append(makeIcon('danger'));
      else marker.textContent = String(i + 1);
      // The glyph repeats what the label and `aria-current` already say.
      marker.setAttribute('aria-hidden', 'true');

      const body = document.createElement('span');
      body.className = 'body';
      const label = document.createElement('span');
      label.className = 'label';
      label.setAttribute('part', 'label');
      label.textContent = step.label;
      body.append(label);
      if (step.description) {
        const desc = document.createElement('span');
        desc.className = 'description';
        desc.setAttribute('part', 'description');
        desc.textContent = step.description;
        body.append(desc);
      }

      inner.append(marker, body);
      item.append(inner);
      drawn.push(item);

      if (i < this.stepList.length - 1) {
        const line = document.createElement('li');
        line.className = 'connector';
        line.setAttribute('part', 'connector');
        line.setAttribute('aria-hidden', 'true');
        line.dataset['state'] = state;
        drawn.push(line);
      }
    });

    this.listEl.replaceChildren(...drawn);
  }
}

define('caos-stepper', CaosStepper);
